import {
  buildMinuteUpdownSlug,
  buildDailyUpdownSlugFromStart,
  toUpdownSeriesCollSlug,
  toUpdownRoundCollSlug,
} from '@/domain/crypto/crypto-minute-slug.js';
import {
  buildCryptoUpdownSameRoundQuery,
  buildCryptoUpdownSlugPageQuery,
  buildCryptoUpdownViewingSeriesQuery,
  enrichUpdownRound,
  isUpdownRoundTsNear,
  mapRawToUpdownRound,
  parseUpdownIntervalSeconds,
  pickUpdownRoundFromRows,
  resolveUpdownRoundTs,
  resolveUpdownRoundNearToleranceSec,
} from '@/domain/crypto/crypto-updown-series.js';
import { fetchCryptoUpdownSlugPage } from '@/lib/crypto/fetchCryptoUpdownSlugPage.js';
import { fetchGammaMarketBySlug } from '@/lib/polymarket/fetchGammaMarkets.js';

function mergeCurrentCollection(rawRows, collection, collectionId, targetRoundTs = 0, intervalSec = 300) {
  const rows = Array.isArray(rawRows) ? [...rawRows] : [];
  if (!collection || typeof collection !== 'object') return rows;

  const cid = String(collection?.id ?? collectionId ?? '').trim();
  if (!cid) return rows;

  const targetTs = Number(targetRoundTs);
  if (Number.isFinite(targetTs) && targetTs > 0) {
    const currentTs = resolveUpdownRoundTs(mapRawToUpdownRound(collection));
    const nearTol = resolveUpdownRoundNearToleranceSec(intervalSec);
    // 当前页是历史档时，勿把旧 collection 插进候选，避免 pick 误命中相邻窗
    if (currentTs > 0 && !isUpdownRoundTsNear(currentTs, targetTs, nearTol)) {
      return rows;
    }
  }

  const exists = rows.some((row) => String(row?.id ?? row?.collectionId ?? '').trim() === cid);
  if (!exists) {
    rows.unshift(collection);
    return rows;
  }

  const idx = rows.findIndex((row) => String(row?.id ?? row?.collectionId ?? '').trim() === cid);
  if (idx >= 0) {
    rows[idx] = { ...collection, ...rows[idx] };
  }
  return rows;
}

function resolveSeriesAssetInterval(seriesKey, intervalSec) {
  const key = toUpdownSeriesCollSlug(seriesKey) || String(seriesKey ?? '').trim().toLowerCase();
  const interval = Math.max(Number(intervalSec) || 300, 60);
  const seriesMatch = key.match(/^([a-z0-9]+)-updown-(\d+[mh])$/i);
  const isDailySeries = /-up-or-down$/i.test(key);
  const asset = seriesMatch?.[1] || (isDailySeries ? key.split('-')[0] : key.split('-')[0]) || '';
  const intervalLabel =
    seriesMatch?.[2] ||
    (isDailySeries ? '1d' : interval >= 3600 ? `${Math.round(interval / 3600)}h` : `${Math.round(interval / 60)}m`);

  return { key, asset, intervalLabel, interval, isDailySeries };
}

function buildFullSlugForRound(seriesKey, roundTs, intervalSec) {
  const { key, asset, intervalLabel, isDailySeries } = resolveSeriesAssetInterval(seriesKey, intervalSec);
  const ts = Number(roundTs);
  if (!key || !Number.isFinite(ts) || ts <= 0) return '';

  if (isDailySeries) return buildDailyUpdownSlugFromStart(key, ts);
  if (asset && intervalLabel && intervalLabel !== '1d') {
    return buildMinuteUpdownSlug(asset, intervalLabel, ts);
  }
  return toUpdownRoundCollSlug(key, ts);
}

function buildFetchQueries(seriesKey, roundTs, intervalSec, { compact = false } = {}) {
  const { key, interval } = resolveSeriesAssetInterval(seriesKey, intervalSec);
  const ts = Number(roundTs);
  if (!key || !Number.isFinite(ts) || ts <= 0) return [];

  const nowSec = Math.floor(Date.now() / 1000);

  /** @type {{ collSlug: string, startTime: number, endTime: number }[]} */
  // 同档：完整 slug；系列窗：前缀
  // 单档时间：liveStart-1 → liveStart+interval
  const roundCollSlug = toUpdownRoundCollSlug(key, ts) || buildFullSlugForRound(key, ts, interval);
  const queries = compact
    ? [
        buildCryptoUpdownSameRoundQuery(key, ts, interval),
        roundCollSlug
          ? {
              collSlug: roundCollSlug,
              startTime: ts - 1,
              endTime: ts + interval,
            }
          : null,
        buildCryptoUpdownViewingSeriesQuery(key, ts, interval, { before: 0, after: 3 }),
      ]
    : [
        buildCryptoUpdownSameRoundQuery(key, ts, interval),
        roundCollSlug
          ? {
              collSlug: roundCollSlug,
              startTime: ts - 1,
              endTime: ts + interval,
            }
          : null,
        buildCryptoUpdownSlugPageQuery(key, ts, interval),
        {
          collSlug: key,
          startTime: ts - interval - 1,
          endTime: ts + interval * 2,
        },
        buildCryptoUpdownViewingSeriesQuery(key, ts, interval, { before: 0, after: 3 }),
        buildCryptoUpdownViewingSeriesQuery(key, ts, interval, { before: 2, after: 2 }),
        buildCryptoUpdownSlugPageQuery(key, nowSec, interval),
      ];

  const seen = new Set();
  return queries.filter(Boolean).filter((query) => {
    const signature = `${query.collSlug}|${query.startTime}|${query.endTime}`;
    if (seen.has(signature)) return false;
    seen.add(signature);
    return true;
  });
}

function pickRoundForNavigation(rows, roundTs, intervalSec, seriesKey) {
  const interval = Math.max(Number(intervalSec) || 300, 60);
  const targetTs = Number(roundTs);
  if (!Number.isFinite(targetTs) || targetTs <= 0) return null;

  const strict = pickUpdownRoundFromRows(rows, targetTs, interval, seriesKey, { strict: true });
  if (strict?.collectionId) return strict;

  const relaxed = pickUpdownRoundFromRows(rows, targetTs, interval, seriesKey, { strict: false });
  const nearTol = resolveUpdownRoundNearToleranceSec(interval);
  if (relaxed?.collectionId && isUpdownRoundTsNear(resolveUpdownRoundTs(relaxed), targetTs, nearTol)) {
    return relaxed;
  }

  const mapped = (rows ?? []).map(mapRawToUpdownRound).filter((row) => row.collectionId);
  return (
    mapped.find((row) => {
      const ts = resolveUpdownRoundTs(row);
      return ts > 0 && isUpdownRoundTsNear(ts, targetTs, nearTol);
    }) ?? null
  );
}

async function fetchRoundFromQuery(
  query,
  { roundTs, interval, seriesKey, collection, collectionId, nowSec, signal, bypassCache = false }
) {
  if (signal?.aborted) return null;
  const batch = await fetchCryptoUpdownSlugPage(query, {
    pageSize: 50,
    fetchAll: true,
    bypassCache,
  });
  const rows = batch.rows ?? [];
  if (!rows.length) return null;

  const merged = mergeCurrentCollection(rows, collection, collectionId, roundTs, interval);
  const picked = pickRoundForNavigation(merged, roundTs, interval, seriesKey);
  if (!picked?.collectionId) return null;
  return enrichUpdownRound(picked, nowSec, interval);
}

/**
 * 按 roundTs 拉取同系列单档（go-live / 时间轴跳转）
 * @param {object} [options]
 * @param {boolean} [options.compact] 少 query，供 go-live 首轮
 * @param {boolean} [options.bypassCache]
 * @param {AbortSignal} [options.signal]
 */
export async function fetchUpdownRoundByTs(
  { seriesKey, roundTs, intervalSec, collection = null, collectionId = '' } = {},
  options = {}
) {
  const key =
    toUpdownSeriesCollSlug(seriesKey) ||
    String(seriesKey ?? '')
      .trim()
      .toLowerCase();
  const ts = Number(roundTs);
  const interval = Math.max(Number(intervalSec) || parseUpdownIntervalSeconds('5m'), 60);
  if (!key || !Number.isFinite(ts) || ts <= 0) return null;

  const nowSec = Math.floor(Date.now() / 1000);
  const queries = buildFetchQueries(key, ts, interval, { compact: Boolean(options.compact) });
  const bypassCache = Boolean(options.bypassCache);

  try {
    for (let i = 0; i < queries.length; i += 3) {
      if (options.signal?.aborted) return null;
      const batch = queries.slice(i, i + 3);
      const results = await Promise.all(
        batch.map((query) =>
          fetchRoundFromQuery(query, {
            roundTs: ts,
            interval,
            seriesKey: key,
            collection,
            collectionId,
            nowSec,
            signal: options.signal,
            bypassCache,
          }).catch(() => null)
        )
      );
      const hit = results.find(Boolean);
      if (hit) return hit;
    }
  } catch (err) {
    console.warn('[crypto-updown] fetch round by ts failed', { seriesKey: key, roundTs: ts }, err);
  }

  return null;
}

/**
 * cryptoPageBySlug 未命中时：先放宽本地查询；再可选 Gamma 探针窄查。
 * Gamma 失败不得阻断本地放宽（历史→live 常见）。
 * @param {object} [options]
 * @param {{ localMissingAfterGamma?: boolean }} [options.out] 诊断：Gamma 有盘但本地仍无 id
 * @returns {Promise<object|null>}
 */
export async function resolveUpdownRoundByTs(
  { seriesKey, roundTs, intervalSec, collection = null, collectionId = '' } = {},
  options = {}
) {
  const params = { seriesKey, roundTs, intervalSec, collection, collectionId };
  if (options.out && typeof options.out === 'object') {
    options.out.localMissingAfterGamma = false;
  }

  const compactHit =
    (await fetchUpdownRoundByTs(params, {
      ...options,
      compact: options.compact !== false,
      bypassCache: Boolean(options.bypassCache),
    })) ?? null;
  if (compactHit?.collectionId) return compactHit;

  // compact 未命中：无论 Gamma 是否可用，先放宽本地扫一遍
  const wideHit = await fetchUpdownRoundByTs(params, {
    ...options,
    compact: false,
    bypassCache: true,
  });
  if (wideHit?.collectionId) return wideHit;

  const { key, asset, interval } = resolveSeriesAssetInterval(seriesKey, intervalSec);
  const ts = Number(roundTs);
  if (!key || !Number.isFinite(ts) || ts <= 0) return null;
  if (options.signal?.aborted) return null;

  const fullSlug = buildFullSlugForRound(key, ts, interval);
  if (!fullSlug) return null;

  let gamma = null;
  try {
    gamma = await fetchGammaMarketBySlug(fullSlug);
  } catch (err) {
    console.warn('[crypto-updown] gamma probe for round failed', { fullSlug }, err);
  }
  if (!gamma) return null;

  const nowSec = Math.floor(Date.now() / 1000);
  const roundCollSlug = toUpdownRoundCollSlug(key, ts) || fullSlug;
  const narrowQueries = [
    roundCollSlug
      ? {
          collSlug: roundCollSlug,
          startTime: ts - 1,
          endTime: ts + interval,
        }
      : null,
    buildCryptoUpdownSameRoundQuery(key, ts, interval),
  ].filter(Boolean);

  for (const query of narrowQueries) {
    if (options.signal?.aborted) return null;
    const picked = await fetchRoundFromQuery(query, {
      roundTs: ts,
      interval,
      seriesKey: key,
      collection,
      collectionId,
      nowSec,
      signal: options.signal,
      bypassCache: true,
    }).catch(() => null);
    if (picked?.collectionId) return picked;
  }

  console.warn('[crypto-updown] gamma has market but local collection missing', {
    fullSlug,
    roundTs: ts,
    asset,
  });
  if (options.out && typeof options.out === 'object') {
    options.out.localMissingAfterGamma = true;
  }
  return null;
}
